import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import workflowService from '../../services/workflow.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { fullName } from '../../lib/fullName';
import { humanize } from '../../lib/stringCase';
import { colors } from '../../styles/theme';
import type { CaseListItem, ListCasesFilters } from '../../types/workflow';
import { readerLocale } from '../../i18n/language';

interface Props {
  /** Filter spec baked in by the parent page — agent vs manager queues
   *  inject different defaults here so the same component renders both. */
  defaultFilters: ListCasesFilters;
  /** Where each row navigates to when clicked. The {caseId} token is
   *  replaced with the row's case id. */
  detailPathPattern: string;
  /** Optional empty-state copy for when there's nothing in the queue. */
  emptyHint?: string;
}

const STATE_BADGE: Record<string, { bg: string; fg: string }> = {
  // contract_review_v1
  pending_upload:       { bg: colors.warningBg, fg: colors.warning },
  uploaded:             { bg: '#EEF2FF',         fg: '#4338CA' },
  returned_to_client:   { bg: colors.dangerBg,   fg: colors.danger },
  pending_mgr:          { bg: '#EEF2FF',         fg: '#4338CA' },
  returned_to_agent:    { bg: '#EEF2F7',         fg: '#475569' },
  approved:             { bg: colors.successBg,  fg: colors.success },
  rejected:             { bg: colors.dangerBg,   fg: colors.danger },
  // onboarding_retail (KYC)
  registered:           { bg: '#EEF2F7',         fg: '#475569' },
  pending_profile:      { bg: '#EEF2F7',         fg: '#475569' },
  pending_pep_aml:      { bg: '#EEF2F7',         fg: '#475569' },
  pending_documents:    { bg: '#EEF2F7',         fg: '#475569' },
  under_review:         { bg: '#EEF2FF',         fg: '#4338CA' },
  information_required: { bg: colors.warningBg,  fg: colors.warning },
  restricted:           { bg: colors.warningBg,  fg: colors.warning },
  archived:             { bg: '#EEF2F7',         fg: '#475569' },
};

const FALLBACK_BADGE = { bg: '#EEF2F7', fg: '#475569' };

function badgeFor(stateSlug: string): { bg: string; fg: string } {
  return STATE_BADGE[stateSlug] ?? FALLBACK_BADGE;
}

function formatRelative(iso: string): string {
  try {
    return new Date(iso).toLocaleString(readerLocale(), {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/**
 * Generic review queue. The two staff dashboards (agent / manager) wrap
 * this with their own default filters. The component owns:
 *   - data fetching with filter state
 *   - "Only my action" toggle (filters by `needs_my_action` client-side)
 *   - pagination
 *   - row click → navigate to detail
 *
 * It does NOT own:
 *   - role-aware filter defaults (parent provides)
 *   - case-detail rendering (separate page)
 *   - transition actions (modal lives on the detail page)
 */
export default function ReviewQueue({
  defaultFilters,
  detailPathPattern,
  emptyHint,
}: Readonly<Props>): JSX.Element {
  const navigate = useNavigate();
  const [filters] = useState<ListCasesFilters>(defaultFilters);
  const [page, setPage] = useState<number>(1);
  const [data, setData] = useState<{ items: CaseListItem[]; total: number; size: number } | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [onlyMyAction, setOnlyMyAction] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    workflowService
      .listCases({ ...filters, page, size: 25 })
      .then((res) => {
        if (cancelled) return;
        setData({ items: res.items, total: res.total, size: res.size });
        setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(getApiErrorMessage(err, 'Could not load the queue.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters, page]);

  const allItems = data?.items ?? [];
  const visibleItems = onlyMyAction
    ? allItems.filter((item) => item.needs_my_action)
    : allItems;

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.size)) : 1;

  return (
    <>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <label className="flex items-center gap-2 text-sm" style={{ color: colors.inkSoft }}>
          <input
            type="checkbox"
            checked={onlyMyAction}
            onChange={(e) => setOnlyMyAction(e.target.checked)}
          />
          {/* */}Show only items needing my action
        </label>
        <div className="text-xs" style={{ color: colors.mutedSoft }}>
          {data ? `${data.total} total · page ${page} of ${totalPages}` : ''}
        </div>
      </div>

      {error && (
        <div
          className="rounded-lg p-4 mb-4 text-sm"
          style={{
            background: colors.dangerBg,
            color: colors.danger,
            border: `1px solid ${colors.dangerBorder}`,
          }}
          role="alert"
        >
          {error}
        </div>
      )}

      {loading && !data && (
        <p className="text-sm" style={{ color: colors.muted }}>
          Loading queue…
        </p>
      )}

      {!loading && data && visibleItems.length === 0 && (
        <div
          className="rounded-2xl p-6 text-sm"
          style={{
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            color: colors.muted,
          }}
        >
          {emptyHint ?? 'Nothing in your queue right now.'}
        </div>
      )}

      {visibleItems.length > 0 && (
        <div
          className="overflow-hidden rounded-2xl"
          style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
        >
          {/* Horizontal scroll so a wide queue never breaks the page layout
              on narrow viewports (it scrolls within the card instead). */}
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead style={{ background: colors.canvas, color: colors.muted }}>
              <tr>
                <th className="text-left font-medium px-4 py-3">Member</th>
                <th className="text-left font-medium px-4 py-3">Workflow</th>
                <th className="text-left font-medium px-4 py-3">State</th>
                <th className="text-left font-medium px-4 py-3">Assigned</th>
                <th className="text-left font-medium px-4 py-3">Last activity</th>
                <th className="text-left font-medium px-4 py-3 sr-only">Action</th>
              </tr>
            </thead>
            <tbody>
              {visibleItems.map((item, idx) => {
                const badge = badgeFor(item.current_state_slug);
                const detailHref = detailPathPattern.replace('{caseId}', item.case_id);
                return (
                  <tr
                    key={item.case_id}
                    onClick={() => navigate(detailHref)}
                    className="cursor-pointer transition-colors"
                    style={{
                      borderTop: idx === 0 ? 'none' : `1px solid ${colors.borderSoft}`,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = colors.canvas;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '';
                    }}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium" style={{ color: colors.ink }}>
                        {fullName(item.user)}
                      </div>
                      <div className="text-xs" style={{ color: colors.mutedSoft }}>
                        {item.user.email}
                      </div>
                    </td>
                    <td className="px-4 py-3" style={{ color: colors.inkSoft }}>
                      {item.workflow_slug}
                      <span className="text-xs ml-1" style={{ color: colors.mutedSoft }}>
                        v{item.workflow_version}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className="inline-block px-2 py-0.5 rounded text-xs font-medium"
                        style={{ background: badge.bg, color: badge.fg }}
                      >
                        {humanize(item.current_state_slug)}
                      </span>
                    </td>
                    <td className="px-4 py-3" style={{ color: colors.inkSoft }}>
                      {item.assigned_to ? (
                        <span className="inline-flex items-center gap-2 flex-wrap">
                          <span>{fullName(item.assigned_to)}</span>
                          {item.assignee_unavailable && (
                            <span
                              className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide"
                              style={{ background: colors.warningBg, color: colors.warning }}
                              title="The assigned agent is currently on time-off. Consider reassigning if the case is urgent."
                            >
                              On leave
                            </span>
                          )}
                        </span>
                      ) : (
                        <span style={{ color: colors.mutedSoft }}>Unassigned</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs" style={{ color: colors.muted }}>
                      {formatRelative(item.last_activity_at)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-xs" style={{ color: colors.gold }}>
                        Open →
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {data && totalPages > 1 && (
        <div className="flex items-center justify-end gap-2 mt-4">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || loading}
            className="px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
            style={{
              background: colors.surface,
              color: colors.inkSoft,
              border: `1px solid ${colors.border}`,
              opacity: page <= 1 ? 0.5 : 1,
            }}
          >
            ← Previous
          </button>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages || loading}
            className="px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
            style={{
              background: colors.surface,
              color: colors.inkSoft,
              border: `1px solid ${colors.border}`,
              opacity: page >= totalPages ? 0.5 : 1,
            }}
          >
            Next →
          </button>
        </div>
      )}
    </>
  );
}
